import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, MapPin, CheckCircle, ArrowRight, Clock, Shield, Award,
  Camera, Zap, Network, Lock, Wifi, Radio, Cable, ClipboardCheck,
  HelpCircle, Navigation, Layers
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { services, cities, getNearbyCities } from '../data/seoPages';
import { generateFaqsForPage, generateMetaDescription } from '../data/seoFaqs';
import Breadcrumbs from '../components/seo/Breadcrumbs';
import SEOHead from '../components/seo/SEOHead';

const iconMap = {
  Camera: Camera,
  Zap: Zap,
  Network: Network,
  Lock: Lock,
  Shield: Shield,
  Wifi: Wifi,
  Radio: Radio,
  Cable: Cable,
  ClipboardCheck: ClipboardCheck
};

export default function ServiceLocationPage() {
  const { slug } = useParams();
  
  // Find matching service and city by trying all combinations
  let service = null;
  let city = null;
  
  for (const s of services) {
    for (const c of cities) {
      if (slug === `${s.slug}-${c.slug}`) {
        service = s;
        city = c;
        break;
      }
    }
    if (service) break;
  }

  // Generate FAQs for this service/city combination
  const faqs = service && city ? generateFaqsForPage(service.slug, city.name, city.region) : [];
  
  // Get nearby cities for Hub & Spoke linking
  const nearbyCities = city ? getNearbyCities(city.slug) : [];
  
  // Update document title and meta tags + inject JSON-LD schemas
  useEffect(() => {
    if (service && city) {
      const pageTitle = `${service.name} ${city.name}, ${city.state} | IQES Low Voltage`;
      
      // Generate optimized meta description per service type
      const metaDescription = generateMetaDescription(service.slug, city.name) || 
        `Professional ${service.name.toLowerCase()} services in ${city.name}, Florida. Licensed & insured contractor. 13+ years experience. Free quotes. Call 786-981-3760.`;
      
      document.title = pageTitle;
      
      // Update meta description
      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', metaDescription);
      }
      
      // Update canonical
      let canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', `https://www.iqeslowvoltage.com/${service.slug}-${city.slug}`);
      }
      
      // Remove existing JSON-LD scripts (to avoid duplicates on navigation)
      document.querySelectorAll('script[data-seo-schema]').forEach(el => el.remove());
      
      // Inject FAQPage Schema (for rich snippets in Google)
      if (faqs.length > 0) {
        const faqSchema = {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        };
        
        const faqScript = document.createElement('script');
        faqScript.type = 'application/ld+json';
        faqScript.setAttribute('data-seo-schema', 'faq');
        faqScript.textContent = JSON.stringify(faqSchema);
        document.head.appendChild(faqScript);
      }
      
      // Inject Service Schema (enhanced LocalBusiness + Service)
      const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": service.name,
        "name": `${service.name} in ${city.name}, FL`,
        "description": metaDescription,
        "provider": {
          "@type": "LocalBusiness",
          "name": "IQES Low Voltage Solutions",
          "image": "https://www.iqeslowvoltage.com/logo-iqes-new.png",
          "telephone": "+1-786-981-3760",
          "email": "info@iqeslowvoltage.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": city.name,
            "addressRegion": "FL",
            "addressCountry": "US"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": getCityCoordinates(city.slug).lat,
            "longitude": getCityCoordinates(city.slug).lng
          },
          "url": "https://www.iqeslowvoltage.com",
          "priceRange": "$$",
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "08:00",
            "closes": "18:00"
          }
        },
        "areaServed": {
          "@type": "City",
          "name": city.name,
          "containedInPlace": {
            "@type": "State",
            "name": "Florida"
          }
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": `${service.name} Services`,
          "itemListElement": service.features.map((feature, idx) => ({
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": feature
            }
          }))
        }
      };
      
      const serviceScript = document.createElement('script');
      serviceScript.type = 'application/ld+json';
      serviceScript.setAttribute('data-seo-schema', 'service');
      serviceScript.textContent = JSON.stringify(serviceSchema);
      document.head.appendChild(serviceScript);
      
      // Inject BreadcrumbList Schema
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://www.iqeslowvoltage.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Services",
            "item": "https://www.iqeslowvoltage.com/services"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": `${service.name} ${city.name}`,
            "item": `https://www.iqeslowvoltage.com/${service.slug}-${city.slug}`
          }
        ]
      };
      
      const breadcrumbScript = document.createElement('script');
      breadcrumbScript.type = 'application/ld+json';
      breadcrumbScript.setAttribute('data-seo-schema', 'breadcrumb');
      breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
      document.head.appendChild(breadcrumbScript);
      
      // Cleanup on unmount
      return () => {
        document.querySelectorAll('script[data-seo-schema]').forEach(el => el.remove());
      };
    }
  }, [service, city, slug, faqs]);
  
  // City coordinates for geo data in schema
  function getCityCoordinates(citySlug) {
    const coords = {
      'miami': { lat: 25.7617, lng: -80.1918 },
      'orlando': { lat: 28.5383, lng: -81.3792 },
      'tampa': { lat: 27.9506, lng: -82.4572 },
      'fort-lauderdale': { lat: 26.1224, lng: -80.1373 },
      'jacksonville': { lat: 30.3322, lng: -81.6557 },
      'west-palm-beach': { lat: 26.7153, lng: -80.0534 },
      'tallahassee': { lat: 30.4383, lng: -84.2807 },
      'panama-city': { lat: 30.1588, lng: -85.6602 },
      'fort-myers': { lat: 26.6406, lng: -81.8723 },
      'winter-haven': { lat: 28.0222, lng: -81.7329 },
      'naples': { lat: 26.1420, lng: -81.7948 },
      'sarasota': { lat: 27.3364, lng: -82.5307 },
      'gainesville': { lat: 29.6516, lng: -82.3248 },
      'ocala': { lat: 29.1872, lng: -82.1401 },
      'daytona-beach': { lat: 29.2108, lng: -81.0228 },
      'boca-raton': { lat: 26.3683, lng: -80.1289 },
      'coral-springs': { lat: 26.2712, lng: -80.2706 },
      'pompano-beach': { lat: 26.2379, lng: -80.1248 },
      'hollywood': { lat: 26.0112, lng: -80.1495 },
      'hialeah': { lat: 25.8576, lng: -80.2781 }
    };
    return coords[citySlug] || { lat: 27.6648, lng: -81.5158 }; // Default to center of Florida
  }
  
  if (!service || !city) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800">Page Not Found</h1>
          <Link to="/" className="text-orange-500 hover:underline mt-4 block">Return to Home</Link>
        </div>
      </div>
    );
  }

  const IconComponent = iconMap[service.icon] || Shield;
  
  // Generate SEO meta description
  const seoDescription = generateMetaDescription(service.slug, city.name) || 
    `Professional ${service.name.toLowerCase()} services in ${city.name}, Florida. Licensed & insured contractor. 13+ years experience. Free quotes. Call 786-981-3760.`;

  return (
    <div className="pt-24">
      {/* SEO Head - Dynamic Open Graph */}
      <SEOHead 
        title={`${service.name} ${city.name}, ${city.state}`}
        description={seoDescription}
        image="https://www.iqeslowvoltage.com/logo-iqes-new.png"
      />
      
      {/* Visual Breadcrumbs */}
      <Breadcrumbs />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0a1628] via-[#1a2d4a] to-[#0f2341] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-4 py-2 rounded-full mb-6">
              <MapPin className="w-4 h-4" />
              <span className="text-sm font-medium">Serving {city.name}, {city.state}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {service.name} in <span className="text-orange-500">{city.name}</span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              {service.description}. Professional installation by licensed technicians with 13+ years of experience.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quote">
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg">
                  Get Free Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:+17869813760">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  <Phone className="mr-2 w-5 h-5" />
                  786-981-3760
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Our {service.shortName} Services in {city.name}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We provide comprehensive {service.name.toLowerCase()} solutions for businesses and residences throughout {city.name} and {city.region}.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {service.features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{feature}</h3>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              Why Choose IQES for {service.shortName} in {city.name}?
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="font-bold text-xl mb-2">13+ Years Experience</h3>
                <p className="text-gray-600">Trusted by hundreds of businesses throughout Florida</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="font-bold text-xl mb-2">Licensed & Insured</h3>
                <p className="text-gray-600">Fully licensed low voltage contractor in Florida</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="font-bold text-xl mb-2">Fast Response</h3>
                <p className="text-gray-600">Quick turnaround on quotes and installations</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nearby Cities Section - Hub & Spoke SEO */}
      {nearbyCities.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-600 px-4 py-2 rounded-full mb-4">
                  <Navigation className="w-4 h-4" />
                  <span className="text-sm font-medium">Nearby Service Areas</span>
                </div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  {service.shortName} Services Near {city.name}
                </h2>
                <p className="text-gray-600">
                  We also provide professional {service.name.toLowerCase()} in these nearby {city.region} cities:
                </p>
              </div>
              
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {nearbyCities.map((nearbyCity, index) => (
                  <motion.div
                    key={nearbyCity.slug}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link 
                      to={`/${service.slug}-${nearbyCity.slug}`}
                      className="block bg-gray-50 hover:bg-orange-50 border border-gray-200 hover:border-orange-300 rounded-xl p-4 text-center transition-all group"
                      title={`${service.name} in ${nearbyCity.name}, FL`}
                    >
                      <MapPin className="w-5 h-5 text-gray-400 group-hover:text-orange-500 mx-auto mb-2 transition-colors" />
                      <span className="font-medium text-gray-800 group-hover:text-orange-600 transition-colors">
                        {nearbyCity.name}
                      </span>
                      <span className="block text-xs text-gray-500 mt-1">{nearbyCity.region}</span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* All Florida Service Areas - Full Hub */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              All {service.shortName} Service Areas in Florida
            </h2>
            <p className="text-gray-600 mb-8">
              IQES provides {service.name.toLowerCase()} services throughout the state of Florida:
            </p>
            
            <div className="flex flex-wrap justify-center gap-3">
              {cities.map((c) => (
                <Link 
                  key={c.slug}
                  to={`/${service.slug}-${c.slug}`}
                  className={`px-4 py-2 rounded-full transition-all ${
                    c.slug === city.slug 
                      ? 'bg-orange-500 text-white font-semibold shadow-md' 
                      : 'bg-white text-gray-700 hover:bg-orange-100 hover:text-orange-700 border border-gray-200'
                  }`}
                  title={`${service.name} in ${c.name}, Florida`}
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Services - Cross-linking */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-600 px-4 py-2 rounded-full mb-4">
                <Layers className="w-4 h-4" />
                <span className="text-sm font-medium">Related Services</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                Other Low Voltage Services in {city.name}
              </h2>
              <p className="text-gray-600">
                Explore our complete range of professional low voltage solutions available in {city.name}, FL
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {services.filter(s => s.slug !== service.slug).map((s, index) => {
                const ServiceIcon = iconMap[s.icon] || Shield;
                return (
                  <motion.div
                    key={s.slug}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link 
                      to={`/${s.slug}-${city.slug}`}
                      className="flex items-center gap-3 bg-gray-50 hover:bg-orange-50 border border-gray-200 hover:border-orange-300 rounded-xl p-4 transition-all group"
                      title={`${s.name} in ${city.name}, FL`}
                    >
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm group-hover:shadow-md transition-shadow">
                        <ServiceIcon className="w-5 h-5 text-orange-500" />
                      </div>
                      <div>
                        <span className="font-medium text-gray-800 group-hover:text-orange-600 transition-colors block">
                          {s.shortName}
                        </span>
                        <span className="text-xs text-gray-500">{city.name}, FL</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-orange-500 ml-auto transition-colors" />
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 px-4 py-2 rounded-full mb-4">
                  <HelpCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Frequently Asked Questions</span>
                </div>
                <h2 className="text-3xl font-bold text-gray-800">
                  {service.shortName} Questions in {city.name}
                </h2>
              </div>
              
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
                  >
                    <details className="group">
                      <summary className="flex items-center justify-between cursor-pointer p-5 font-semibold text-gray-800 hover:bg-gray-50 transition-colors">
                        <span className="pr-4">{faq.question}</span>
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 text-orange-500 flex items-center justify-center text-sm group-open:rotate-45 transition-transform">
                          +
                        </span>
                      </summary>
                      <div className="px-5 pb-5 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                        {faq.answer}
                      </div>
                    </details>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Contact us today for a free quote on {service.name.toLowerCase()} in {city.name}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote">
              <Button size="lg" className="bg-white text-orange-500 hover:bg-gray-100 px-8 py-6 text-lg">
                Request Free Quote
              </Button>
            </Link>
            <a href="tel:+17869813760">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                <Phone className="mr-2 w-5 h-5" />
                Call 786-981-3760
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
